import { api, type SyncMutation } from "../api";
import { getActiveHouseholdId } from "../households";
import * as clientDb from "../db/clientDb";
import type { PendingMutation } from "../db/clientDb";
import type { MutationType } from "./mutations";
import { compressImageForUpload } from "../utils/compressImage";

export type SyncState = "idle" | "syncing" | "offline" | "error";

const listeners = new Set<(state: SyncState, detail?: string) => void>();
let syncing = false;
let socket: WebSocket | null = null;
let reconnectTimer: number | undefined;
let reconnectDelay = 1000;
const MAX_RECONNECT_DELAY = 30000;

function emit(state: SyncState, detail?: string) {
    for (const listener of listeners) {
        listener(state, detail);
    }
}

export function onSyncStateChange(
    listener: (state: SyncState, detail?: string) => void,
): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function isOnline(): boolean {
    return navigator.onLine;
}

async function registerBackgroundSync(): Promise<void> {
    if (!("serviceWorker" in navigator) || !("SyncManager" in window)) return;
    try {
        const reg = await navigator.serviceWorker.ready;
        const sync = (reg as unknown as { sync?: { register: (tag: string) => Promise<void> } })
            .sync;
        await sync?.register("pantry-pal-push");
    } catch {
        // Background Sync is best-effort and may be denied by the browser.
    }
}

let syncQueued = false;

export async function performSync(): Promise<void> {
    if (!isOnline()) {
        emit("offline");
        void registerBackgroundSync();
        return;
    }

    if (syncing) {
        syncQueued = true;
        return;
    }

    do {
        syncQueued = false;
        syncing = true;
        emit("syncing");

        try {
            const hadChanges = await syncRound();
            emit("idle");
            if (hadChanges) {
                window.dispatchEvent(new CustomEvent("pantry-sync"));
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            emit("error", message);
        } finally {
            syncing = false;
        }
    } while (syncQueued);
}

async function syncRound(): Promise<boolean> {
    const since = await clientDb.getSyncState("syncCursor");
    const cursor = since ? Number(since) : 0;

    const pending = await clientDb.listPendingMutations();
    const images = await clientDb.listPendingImages();
    const imageIds = new Set(images.map((img) => img.mutationId));
    const coalesced = coalesceMutations(pending, imageIds);

    const syncMutations: SyncMutation[] = coalesced.map(({ mutation }) => ({
        type: mutation.type,
        recordId: mutation.recordId,
        fields: mutation.fields,
        clientTimestamp: mutation.clientTimestamp,
    }));

    const res = await api.sync.batch(cursor, syncMutations);

    // Remap temp IDs from server response
    for (const [tempKey, serverId] of Object.entries(res.idMap ?? {})) {
        const tempId = Number(tempKey);
        if (!Number.isNaN(tempId) && tempId < 0) {
            const table = coalesced.find(({ mutation }) => mutation.recordId === tempId)?.mutation
                .table;
            if (table) {
                await remapLocalId(table, tempId, serverId);
            }
        }
    }

    // Apply server changes
    for (const item of res.items) {
        const merged = await mergeServerRecord("items", item as unknown as clientDb.LocalItem);
        if (merged) await clientDb.putItem(merged as clientDb.LocalItem);
    }
    for (const location of res.locations) {
        const merged = await mergeServerRecord(
            "locations",
            location as unknown as clientDb.LocalLocation,
        );
        if (merged) await clientDb.putLocation(merged as clientDb.LocalLocation);
    }
    for (const grocery of res.grocery_items) {
        const merged = await mergeServerRecord(
            "grocery_items",
            grocery as unknown as clientDb.GroceryItem,
        );
        if (merged) await clientDb.putGroceryItem(merged as clientDb.GroceryItem);
    }
    for (const id of res.deleted_items ?? []) {
        await clientDb.deleteItemLocal(id);
    }
    for (const id of res.deleted_locations ?? []) {
        await clientDb.deleteLocationLocal(id);
    }
    for (const id of res.deleted_grocery ?? []) {
        await clientDb.deleteGroceryItemLocal(id);
    }

    if (res.cursor) {
        await clientDb.setSyncState("syncCursor", String(res.cursor));
    }

    // Delete synced mutations
    for (const { sourceIds } of coalesced) {
        for (const id of sourceIds) {
            await clientDb.deleteMutation(id);
        }
    }

    // Upload pending images for synced mutations
    for (const { mutation } of coalesced) {
        const related = images.find((img) => img.mutationId === mutation.id);
        if (related) {
            const pending = await clientDb.getPendingImage(related.id);
            if (!pending) continue;
            try {
                const compressed = await compressImageForUpload(
                    pending.file,
                    pending.kind === "item" ? 1280 : 768,
                );
                const upload = await api.upload[pending.kind](compressed);
                const recordId =
                    typeof mutation.recordId === "number" && mutation.recordId >= 0
                        ? mutation.recordId
                        : (res.idMap?.[String(mutation.recordId)] ?? null);
                if (recordId) {
                    await updateLocalAfterImage(recordId, upload.path, pending.kind);
                }
                await clientDb.deletePendingImage(related.id);
            } catch {
                // Image upload failed — will retry on next sync
            }
        }
    }

    await cacheServerImages(res);

    const hadChanges =
        res.items.length > 0 ||
        res.locations.length > 0 ||
        res.grocery_items.length > 0 ||
        (res.deleted_items ?? []).length > 0 ||
        (res.deleted_locations ?? []).length > 0 ||
        (res.deleted_grocery ?? []).length > 0 ||
        coalesced.length > 0;

    return hadChanges;
}

async function mergeServerRecord(
    table: "items" | "locations" | "grocery_items",
    serverRecord: clientDb.LocalItem | clientDb.LocalLocation | clientDb.GroceryItem,
): Promise<clientDb.LocalItem | clientDb.LocalLocation | clientDb.GroceryItem | null> {
    const id = (serverRecord as { id: number | string }).id;
    if (id === undefined) return serverRecord;

    const pending = await clientDb.listMutationsForRecord(table, id);
    const pendingFields = new Set<string>();
    for (const m of pending) {
        for (const f of Object.keys(m.fields)) {
            pendingFields.add(f);
        }
    }

    let local: clientDb.LocalItem | clientDb.LocalLocation | clientDb.GroceryItem | undefined;
    if (table === "items") local = await clientDb.getItem(Number(id));
    else if (table === "locations") local = await clientDb.getLocation(Number(id));
    else if (table === "grocery_items") local = await clientDb.getGroceryItem(Number(id));

    if (!local) return serverRecord;
    if (pendingFields.size === 0) return serverRecord;

    const merged = { ...serverRecord } as Record<string, unknown>;
    const localRecord = local as unknown as Record<string, unknown>;

    for (const f of pendingFields) {
        if (f in localRecord) {
            merged[f] = localRecord[f];
        }
    }

    const updatedAt = (serverRecord as unknown as Record<string, unknown>).updated_at;
    if (updatedAt !== undefined && !pendingFields.has("updated_at")) {
        merged.updated_at = updatedAt;
    }

    return merged as unknown as clientDb.LocalItem | clientDb.LocalLocation | clientDb.GroceryItem;
}

function coalesceMutations(
    pending: PendingMutation[],
    imageIds: Set<string>,
): { mutation: PendingMutation; sourceIds: string[] }[] {
    const order: string[] = [];
    const map = new Map<string, { mutation: PendingMutation; sourceIds: string[] }>();

    for (const mutation of pending) {
        const key = `${mutation.table}:${mutation.recordId}`;
        const existing = map.get(key);
        if (existing) {
            const merged = mergeMutations(existing, mutation, imageIds);
            if (merged) {
                map.set(key, merged);
            } else {
                const idx = order.indexOf(key);
                if (idx !== -1) order.splice(idx, 1);
                map.delete(key);
            }
        } else {
            const entry = { mutation: { ...mutation }, sourceIds: [mutation.id] };
            if (imageIds.has(mutation.id)) {
                entry.mutation.id = mutation.id;
            }
            map.set(key, entry);
            order.push(key);
        }
    }

    return order.map((k) => map.get(k)!).filter(Boolean);
}

function mergeMutations(
    current: { mutation: PendingMutation; sourceIds: string[] },
    next: PendingMutation,
    imageIds: Set<string>,
): { mutation: PendingMutation; sourceIds: string[] } | null {
    if (isDeleteType(next.type)) {
        if (isCreateType(current.mutation.type)) return null;
        return {
            mutation: { ...next, id: next.id },
            sourceIds: [...current.sourceIds, next.id],
        };
    }

    const finalType = determineFinalType(current.mutation.type, next.type);
    const mergedFields = { ...current.mutation.fields, ...next.fields };
    const sourceIds = [...current.sourceIds, next.id];
    const preferredId = [...sourceIds].reverse().find((id) => imageIds.has(id)) ?? next.id;

    const mutation: PendingMutation = {
        ...current.mutation,
        id: preferredId,
        type: finalType,
        fields: mergedFields,
        clientTimestamp: next.clientTimestamp,
        createdAt: next.createdAt,
    };
    return { mutation, sourceIds };
}

function isDeleteType(type: MutationType): boolean {
    return type === "delete_item" || type === "delete_location" || type === "delete_grocery";
}

function isCreateType(type: MutationType): boolean {
    return type === "create_item" || type === "create_location" || type === "create_grocery";
}

function determineFinalType(aType: MutationType, bType: MutationType): MutationType {
    const normalize = (t: MutationType): MutationType =>
        t === "decrement_item" ? "update_item" : t;
    const an = normalize(aType);
    const bn = normalize(bType);
    if (an.startsWith("create_") && bn.startsWith("update_")) return an;
    return bn;
}

async function remapLocalId(table: string, tempId: number, serverId: number): Promise<void> {
    if (table === "items") {
        const item = await clientDb.getItem(tempId);
        if (item) {
            await clientDb.deleteItemLocal(tempId);
            await clientDb.putItem({ ...item, id: serverId });
        }
    } else if (table === "locations") {
        const location = await clientDb.getLocation(tempId);
        if (location) {
            await clientDb.deleteLocationLocal(tempId);
            await clientDb.putLocation({ ...location, id: serverId });
        }
    } else if (table === "grocery_items") {
        const grocery = await clientDb.getGroceryItem(tempId);
        if (grocery) {
            await clientDb.deleteGroceryItemLocal(tempId);
            await clientDb.putGroceryItem({ ...grocery, id: serverId });
        }
    }

    const all = await clientDb.listPendingMutations();
    for (const m of all) {
        if (m.recordId === tempId) {
            m.recordId = serverId;
            await clientDb.updateMutation(m);
        }
    }

    window.dispatchEvent(
        new CustomEvent("pantry-id-remap", {
            detail: { table, tempId, serverId },
        }),
    );
}

async function updateLocalAfterImage(
    recordId: number,
    path: string,
    kind: "item" | "location",
): Promise<void> {
    if (kind === "item") {
        const item = await clientDb.getItem(recordId);
        if (item) {
            item.image_path = path;
            await clientDb.putItem(item);
        }
        await api.items.update(recordId, { image_path: path });
    } else {
        const location = await clientDb.getLocation(recordId);
        if (location) {
            location.image_path = path;
            await clientDb.putLocation(location);
        }
        await api.locations.update(recordId, { image_path: path });
    }
}

async function cacheServerImages(res: {
    items: { image_path?: string | null }[];
    locations: { image_path?: string | null }[];
}): Promise<void> {
    const cached = await clientDb.listCachedImagePaths();
    const toCache = new Set<string>();
    for (const item of res.items) {
        if (item.image_path && !cached.has(item.image_path)) toCache.add(item.image_path);
    }
    for (const loc of res.locations) {
        if (loc.image_path && !cached.has(loc.image_path)) toCache.add(loc.image_path);
    }
    await Promise.all(
        [...toCache].map(async (path) => {
            try {
                const resp = await fetch(path);
                if (!resp.ok) return;
                const blob = await resp.blob();
                await clientDb.putCachedImage(path, blob);
            } catch {
                // Network error — will retry on next sync
            }
        }),
    );
}

function connectSyncSocket(): void {
    if (socket || !isOnline()) return;

    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${protocol}//${location.host}/api/ws`);
    socket = ws;

    ws.addEventListener("open", () => {
        reconnectDelay = 1000;
        void performSync();
    });

    ws.addEventListener("message", (event) => {
        try {
            const data = JSON.parse(String(event.data));
            if (data.type === "sync") {
                if (
                    typeof data.householdId === "number" &&
                    data.householdId !== getActiveHouseholdId()
                ) {
                    return;
                }
                void performSync();
            }
        } catch {
            // ignore malformed messages
        }
    });

    ws.addEventListener("close", () => {
        socket = null;
        scheduleReconnect();
    });

    ws.addEventListener("error", () => {
        ws.close();
    });
}

function scheduleReconnect(): void {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = window.setTimeout(() => {
        reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY);
        connectSyncSocket();
    }, reconnectDelay);
}

export function startAutoSync(): void {
    window.addEventListener("online", () => {
        connectSyncSocket();
        void performSync();
    });
    window.addEventListener("offline", () => {
        socket?.close();
        socket = null;
        emit("offline");
    });
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
            connectSyncSocket();
            void performSync();
        }
    });
    connectSyncSocket();
    void performSync();
}
